import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import TextField from '@components/TextFieldCaseSetting';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import styled from 'styled-components';
import AgGrid from '@components/AgGrid/AgGrid';
import { LinkCellRenderer } from '@trueprodigy/components';
import { saveCaseHistory } from '@modules/litigation/utils';
import { userDataSelector } from '@selectors/global';
import { useSelector, useDispatch } from 'react-redux';
import {
  preferenceSelector,
} from '@modules/appraisal/selectors/global';
import * as globalActions from '@modules/litigation/actions/global';

const QuickAccessProperty = ({
  history,
  classes,
  quickCaseLookup,
}) => {
  const dispatch = useDispatch();
  const preference = useSelector(preferenceSelector);
  const userData = useSelector(userDataSelector);
  const [isOpen, setOpen] = useState(false);
  const firstKeyPressed = useRef(false);
  const [id, setId] = useState('');
  const [cases, setCases] = useState([]);
  const [isInvalid, setIsInvalid] = useState(false);

  const persistPreference = ({ userId, data }) =>
    dispatch(globalActions.persistPreference({ userId, data }));

  useEffect(() => {
    document.addEventListener('keydown', keyHandler, false);
    return () => document.removeEventListener('keydown', keyHandler, false);
  }, []);

  const keyHandler = e => {
    let active = !(
      document.activeElement.id && document.activeElement.id.match(/cell/gi)
    );
    if (e.code === 'ArrowUp' && active) {
      if (!firstKeyPressed.current) {
        firstKeyPressed.current = true;
      } else {
        setOpen(true);
      }
    } else {
      firstKeyPressed.current = false;
    }

    setTimeout(() => {
      firstKeyPressed.current = false;
    }, 500);
  };

  const openProperty = async () => {
    try {
      const res = await quickCaseLookup({
        payload: {
          lookUp: {
            operator: '=',
            value: id,
          },
          caseType: {
            operator: 'mlike',
            value: 'litigation',
          },
        },
      });
      if (res && res.results && res.results.length === 1) {
        const { caseID } = res.results[0];
        saveCaseHistory(
          caseID,
          userData.get('id'),
          preference,
          persistPreference,
        );
        history.push(`/litigation/litigation-case-maintenance/${caseID}`);
        closeModal();
      } else if (res && res.results && res.results.length > 1) {
        const detailsRes = await quickCaseLookup({
          payload: {
            lookUp: {
              operator: '=',
              value: id,
            },
            caseType: {
              operator: 'mlike',
              value: 'litigation',
            },
          },
        });
        setCases(detailsRes.results);
      } else {
        setIsInvalid(true);
      }
    } catch (error) {
      setIsInvalid(true);
    }
  };

  const closeModal = () => {
    setId('');
    setOpen(false);
    setCases([]);
    setIsInvalid(false);
  };

  const buildColDefs = () => [
    {
      headerName: 'Case ID',
      field: 'caseID',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: (_, data) => {
          const { caseID } = data;
          history.push(`/litigation/litigation-case-maintenance/${caseID}`);
          closeModal();
        },
      },
    },
    {
      headerName: 'Cause #',
      field: 'causeNumber',
    },
    {
      headerName: 'Case Status',
      field: 'caseStatus',
    },
    {
      headerName: 'Prop ID',
      field: 'pIDList',
    },
    {
      headerName: 'Owners',
      field: 'ownerNameList',
    },
  ];

  return (
    <Drawer
      anchor="top"
      open={isOpen}
      onClose={closeModal}
      PaperProps={{ style: { width: '50%', left: 'calc(100vw / 4)' } }}
    >
      <StyledDialogTitle id="form-dialog-title">Case Look-up</StyledDialogTitle>
      <DialogContent style={{ paddingBottom: 0, paddingLeft: 35 }}>
        <div
          style={{
            display: 'flex',
            position: 'relative',
            padding: '20px 0 0 0',
          }}
        >
          <TextField
            autoFocus
            margin="dense"
            id="id"
            type="text"
            style={{ width: '100%' }}
            value={id}
            placeholder="Type Cause Number/pID and Press Enter"
            onChange={e => {
              if (isInvalid) setIsInvalid(false);
              setId(e.target.value);
            }}
            InputProps={{
              classes: { input: classes.input, root: classes.root },
            }}
            onKeyPress={e => {
              if (e.key === 'Enter' && id) {
                openProperty();
                e.preventDefault();
              }
            }}
          />
        </div>
      </DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          justifyContent: isInvalid ? 'space-between' : 'flex-end',
          alignItems: 'center',
          padding: '10px 25px 10px 42px',
        }}
      >
        {isInvalid && <ErrorTip>Invalid Cause Number/pID</ErrorTip>}
        <div>
          <Button onClick={closeModal} color="primary">
            Cancel
          </Button>
          <Button onClick={openProperty} color="primary">
            Lookup
          </Button>
        </div>
      </DialogActions>
      {cases.length > 1 && (
        <div
          style={{
            width: '100%',
            height: '300px',
            padding: '20px',
          }}
          className="ag-theme-material"
        >
          <AgGrid
            autoResizeToFit
            rowData={cases}
            columnDefs={buildColDefs()}
            name="quickCaseLookupListing"
            disablePreferenceSave
            onGridReady={() => { }}
          />
        </div>
      )}
    </Drawer>
  );
};

QuickAccessProperty.propTypes = {
  history: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  year: PropTypes.string,
  selectCaseRecord: PropTypes.func,
  selectCaseRecordYear: PropTypes.func,
  quickCaseLookup: PropTypes.func.isRequired,
};

const ErrorTip = styled.p`
  margin: 0;
  color: red;
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 12px 35px 0 !important;

  > h2 {
    font-family: Roboto, sans-serif;
    font-size: 32px;
    font-weight: bold;
  }
`;

const styles = {
  root: {
    lineHeight: '30px',
  },
  input: {
    '&::placeholder': {
      fontSize: 25,
      fontFamily: 'Arial',
      color: 'rgba(0, 0, 0, 0.16)',
    },
    font: '300 30px Roboto, sans-serif',
  },
};

export default withRouter(withStyles(styles)(QuickAccessProperty));
